'use client'

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
} from 'react'
import {
  Cable,
  Check,
  CheckCircle2,
  ChevronDown,
  Download,
  FileDown,
  FileUp,
  GripVertical,
  Pencil,
  PlugZap,
  RadioTower,
  Search,
  ShieldCheck,
  TriangleAlert,
  Upload,
  Unplug,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  CTCSS_TONES,
  DTCS_CODES,
} from '@/lib/baofeng-web'
import { pendingWebProgrammerRadios } from '@/lib/web-programmer-roadmap'
import {
  BROWSER_RADIO_PROFILES,
  browserApplyChannel,
  browserChannelLabel,
  browserCompareImages,
  browserCreateBackup,
  browserExtractImage,
  browserInsertChannel,
  browserInsertionTarget,
  browserMoveChannel,
  browserParseChannels,
  browserProfileById,
  browserReadRadio,
  browserWriteRadio,
  orderBrowserRadioGroups,
  type RadioProgress,
  type WebChannel,
} from '@/lib/radio-web'
import {
  WebSerialRadioTransport,
  hasWebSerial,
  requestWebSerialPort,
} from '@/lib/web-serial'
import {
  TIDRADIO_HID_FILTER,
  WebHidRadioTransport,
  hasWebHid,
  requestWebHidDevice,
} from '@/lib/web-hid'
import { trackProgrammerEvent } from '@/lib/web-programmer-analytics'

type BusyAction = '' | 'reading' | 'writing' | 'verifying'
type ProgrammerTransport = WebSerialRadioTransport | WebHidRadioTransport

const EMPTY_PROGRESS: RadioProgress = {
  current: 0,
  total: 1,
  label: 'Waiting for radio',
}
const PROFILE_STORAGE_KEY = 'nc_web_programmer_profile'
const SIGNAL_MODES = [
  { dataTerminalReady: true, requestToSend: true },
  { dataTerminalReady: false, requestToSend: false },
  { dataTerminalReady: true, requestToSend: false },
  { dataTerminalReady: false, requestToSend: true },
] as const

function formatFrequency(value: number | null): string {
  if (!value) return '—'
  return (value / 1_000_000).toFixed(6)
}

function parseFrequency(value: string): number | null {
  const frequency = Number.parseFloat(value)
  if (!Number.isFinite(frequency) || frequency <= 0) return null
  return Math.round(frequency * 1_000_000)
}

function messageFor(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

function editableChannel(channel: WebChannel): WebChannel {
  return {
    ...channel,
    ctcss: channel.ctcss ?? CTCSS_TONES[0],
    dtcs: channel.dtcs ?? DTCS_CODES[0],
    extras: channel.extras?.map((extra) => ({ ...extra })),
  }
}

function updateChannelExtra(
  channel: WebChannel,
  name: string,
  value: string | number | boolean,
): WebChannel {
  return {
    ...channel,
    extras: channel.extras?.map((extra) =>
      extra.name === name ? { ...extra, value } : extra,
    ),
  }
}

function safeFilename(value: string): string {
  return value.replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '')
}

export function WebProgrammer() {
  const [profileId, setProfileId] = useState(() => {
    if (typeof window !== 'undefined') {
      try {
        const stored = window.localStorage.getItem(PROFILE_STORAGE_KEY)
        if (stored && BROWSER_RADIO_PROFILES.some((candidate) => candidate.id === stored)) {
          return stored
        }
      } catch {
        // Browser storage can be unavailable in restricted modes.
      }
    }
    return BROWSER_RADIO_PROFILES[0].id
  })
  const profile = useMemo(() => browserProfileById(profileId), [profileId])
  const profileUsesHid = profile.protocol === 'tid-hid'
  const profileReadOnly = profileUsesHid
  const [serialAvailable, setSerialAvailable] = useState(false)
  const [hidAvailable, setHidAvailable] = useState(false)
  const [successfulReads, setSuccessfulReads] = useState<number | null>(null)
  const [connected, setConnected] = useState(false)
  const [reportedModel, setReportedModel] = useState('')
  const [image, setImage] = useState<Uint8Array | null>(null)
  const [baseline, setBaseline] = useState<Uint8Array | null>(null)
  const [channels, setChannels] = useState<WebChannel[]>([])
  const [busy, setBusy] = useState<BusyAction>('')
  const [progress, setProgress] = useState<RadioProgress>(EMPTY_PROGRESS)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [query, setQuery] = useState('')
  const [radioQuery, setRadioQuery] = useState('')
  const [showEmpty, setShowEmpty] = useState(false)
  const [selected, setSelected] = useState<number | null>(null)
  const [editor, setEditor] = useState<WebChannel | null>(null)
  const [placementOpen, setPlacementOpen] = useState(false)
  const [newChannelTarget, setNewChannelTarget] = useState<number | null>(null)
  const [draggedChannel, setDraggedChannel] = useState<number | null>(null)
  const transportRef = useRef<ProgrammerTransport | null>(null)
  const signalModeRef = useRef<(typeof SIGNAL_MODES)[number]>(SIGNAL_MODES[0])
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const profilePickerRef = useRef<HTMLDetailsElement | null>(null)
  const radioSearchRef = useRef<HTMLInputElement | null>(null)
  const visitTrackedRef = useRef(false)

  useEffect(() => {
    setSerialAvailable(hasWebSerial())
    setHidAvailable(hasWebHid())
    if (!visitTrackedRef.current) {
      visitTrackedRef.current = true
      trackProgrammerEvent('visit', profile.model)
    }
    fetch('/api/web-programmer/stats')
      .then(async (response) => {
        if (!response.ok) return null
        return (await response.json()) as { successfulReads?: number }
      })
      .then((data) => {
        if (typeof data?.successfulReads === 'number') {
          setSuccessfulReads(data.successfulReads)
        }
      })
      .catch(() => {})
    return () => {
      void transportRef.current?.close()
    }
  }, [])

  const populatedCount = useMemo(
    () => channels.reduce((count, channel) => count + (channel.empty ? 0 : 1), 0),
    [channels],
  )
  const changed = useMemo(
    () =>
      Boolean(
        image &&
          baseline &&
          browserCompareImages(profile, image, baseline) !== null,
      ),
    [baseline, image, profile],
  )
  const filteredChannels = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return channels.filter((channel) => {
      if (!showEmpty && channel.empty) return false
      if (!needle) return true
      return (
        String(channel.number).includes(needle) ||
        browserChannelLabel(profile, channel.number)
          .toLowerCase()
          .includes(needle) ||
        channel.name.toLowerCase().includes(needle) ||
        formatFrequency(channel.rxFrequency).includes(needle) ||
        formatFrequency(channel.txFrequency).includes(needle)
      )
    })
  }, [channels, profile, query, showEmpty])

  const progressPercent = Math.round(
    Math.max(0, Math.min(1, progress.current / Math.max(1, progress.total))) * 100,
  )
  const radioGroups = useMemo(
    () => orderBrowserRadioGroups([
      {
        label: 'UV-17 family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'uv17',
        ),
      },
      {
        label: 'Classic UV-5R family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'uv5r',
        ),
      },
      {
        label: 'Baofeng / BTECH common clone family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'baofeng-common',
        ),
      },
      {
        label: 'Baofeng UV-B5 / BF-V85 family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'baofeng-uvb5',
        ),
      },
      {
        label: 'Compact RT22 family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'rt22',
        ),
      },
      {
        label: 'T18 family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 't18',
        ),
      },
      {
        label: 'Yaesu FT-60 family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'ft60',
        ),
      },
      {
        label: 'Yaesu SCU-35 family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'sc35',
        ),
      },
      {
        label: 'Yaesu VX-170 family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'vx170',
        ),
      },
      {
        label: 'Retevis RB15 family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'rb15',
        ),
      },
      {
        label: 'Midland MXT295',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'mxt295',
        ),
      },
      {
        label: 'H777 family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'h777',
        ),
      },
      {
        label: 'BTECH/QYT mobile family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'btech',
        ),
      },
      {
        label: 'Retevis RT21 family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'rt21',
        ),
      },
      {
        label: 'Kenwood 60G commercial family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'kenwood60g',
        ),
      },
      {
        label: 'TIDRADIO / Radtel color-screen family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'tdh8',
        ),
      },
      {
        label: 'TIDRADIO 2026 CPS / HID family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'tid-hid',
        ),
      },
      {
        label: 'MML JC8810 commercial family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'jc8810',
        ),
      },
      {
        label: 'GA-510 commercial family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'ga510',
        ),
      },
      {
        label: 'RT-490 / JC-8629 commercial family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'rt490',
        ),
      },
      {
        label: 'BF-T8 / RB27 compact family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'bft8',
        ),
      },
      {
        label: 'TH-9000 / RT9000D mobile family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'th9000',
        ),
      },
      {
        label: 'Feidaxin FD-268 family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'fd268',
        ),
      },
      {
        label: 'Baofeng WP970I waterproof family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'wp970i',
        ),
      },
      {
        label: 'Radtel RT-900 / RT-910 / RT-920 family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'rt900',
        ),
      },
      {
        label: 'AnyTone 778UV / RT95 mobile family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'anytone778',
        ),
      },
      {
        label: 'Alinco DR-x35 / DJ mobile family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'alinco-drx35',
        ),
      },
      {
        label: 'Alinco DR735T',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'alinco-dr735t',
        ),
      },
      {
        label: 'Leixen VV-898 / JT270 family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'leixen',
        ),
      },
      {
        label: 'Kenwood M60 mobile family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'kenwood-m60',
        ),
      },
      {
        label: 'TYT TH-UV88 / Retevis family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'thuv88',
        ),
      },
      {
        label: 'Radioddity R2 / RT24 compact family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'r2',
        ),
      },
      {
        label: 'Yaesu FT-7800 / FT-7900',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'ft7800',
        ),
      },
      {
        label: 'Yaesu FT-8800 / FT-8900',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'yaesu-ftx800',
        ),
      },
      {
        label: 'Kenwood TK-x180 zone family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'kenwood-tkx180',
        ),
      },
      {
        label: 'Kenwood 60-series portables',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'kenwood-p60',
        ),
      },
      {
        label: 'Kenwood TK-x80 family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'kenwood-tkx80',
        ),
      },
      {
        label: 'LUITON LT-725UV / Baojie dual-band family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'lt725uv',
        ),
      },
      {
        label: 'Kenwood live CAT / 2000 / 480 / 590 family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'kenwood-cat',
        ),
      },
      {
        label: 'Icom CI-V live family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'icom-civ',
        ),
      },
      {
        label: 'AnyTone 5888UV family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'anytone5888',
        ),
      },
      {
        label: 'Kenwood TK-x102 mobile family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'kenwood-tkx102',
        ),
      },
      {
        label: 'Kenwood TK-x140 portable family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'kenwood-tkx140',
        ),
      },
      {
        label: 'Kenwood TK-x160 mobile family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'kenwood-tkx160',
        ),
      },
      {
        label: 'Wouxun classic handheld family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'wouxun-classic',
        ),
      },
      {
        label: 'Wouxun KG-935G / XS20 family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'wouxun-kg935g',
        ),
      },
      {
        label: 'Wouxun KG-UV9D / UV9PX family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'wouxun-kguv9d',
        ),
      },
      {
        label: 'Radioddity GM-30 / MU-5 family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'radioddity-gm30',
        ),
      },
      {
        label: 'Quansheng UV-K5 family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'uvk5',
        ),
      },
      {
        label: 'Retevis RA25 / AnyTone 779UV family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'retevis-ra25',
        ),
      },
      {
        label: 'Retevis RT1',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'retevis-rt1',
        ),
      },
      {
        label: 'Retevis RT21H',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'retevis-rt21h',
        ),
      },
      {
        label: 'Retevis RT23',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'retevis-rt23',
        ),
      },
      {
        label: 'Retevis RT87',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'retevis-rt87',
        ),
      },
      {
        label: 'Retevis RT97S',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'retevis-rt97s',
        ),
      },
      {
        label: 'Retevis RB17P',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'retevis-rb17p',
        ),
      },
      {
        label: 'Rugged RH5R-V2',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'rugged-rh5rv2',
        ),
      },
      {
        label: 'Yaesu FT-817 / FT-818 family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'yaesu-ft817',
        ),
      },
      {
        label: 'Baofeng W31D / BF-T20D / BF-V12D',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'baofeng-digital',
        ),
      },
      {
        label: 'Baofeng DM-32UV DMR',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'baofeng-dm32uv',
        ),
      },
      {
        label: 'Yaesu FT-1D / FT2D / FT3D / FT5D family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'yaesu-ft2d',
        ),
      },
      {
        label: 'TYT TH-7800 / TH-7900 / TH-9800 / Retevis MA1 family',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'tyt-th7800',
        ),
      },
      {
        label: 'TYT TH-UVF8D',
        profiles: BROWSER_RADIO_PROFILES.filter(
          (candidate) => candidate.protocol === 'tyt-thuvf8d',
        ),
      },
    ]),
    [],
  )
  const roadmapRadios = useMemo(
    () => pendingWebProgrammerRadios(BROWSER_RADIO_PROFILES),
    [],
  )
  const filteredRadioGroups = useMemo(() => {
    const needle = radioQuery.trim().toLowerCase()
    if (!needle) return radioGroups
    return radioGroups
      .map((group) => ({
        ...group,
        profiles: group.profiles.filter((candidate) =>
          `${candidate.vendor} ${candidate.model}`.toLowerCase().includes(needle),
        ),
      }))
      .filter((group) => group.profiles.length > 0)
  }, [radioGroups, radioQuery])

  function clearRadioState(): void {
    setConnected(false)
    setReportedModel('')
    setImage(null)
    setBaseline(null)
    setChannels([])
    setSelected(null)
    setError('')
    setNotice('')
    setQuery('')
    setPlacementOpen(false)
    setNewChannelTarget(null)
    setEditor(null)
    setDraggedChannel(null)
    setProgress(EMPTY_PROGRESS)
  }

  async function disconnect(): Promise<void> {
    const transport = transportRef.current
    transportRef.current = null
    setConnected(false)
    if (transport) await transport.close()
    setNotice('Radio connection closed. Your loaded image remains available for offline editing.')
  }

  async function discardImage(): Promise<void> {
    if (changed && !window.confirm('Discard the current edited image?')) return
    await transportRef.current?.close()
    transportRef.current = null
    clearRadioState()
  }

  async function ensureTransport(): Promise<ProgrammerTransport> {
    if (transportRef.current) return transportRef.current
    if (profile.protocol === 'tid-hid') {
      const device = await requestWebHidDevice([TIDRADIO_HID_FILTER])
      const transport = new WebHidRadioTransport(device)
      transportRef.current = transport
      return transport
    }
    const port = await requestWebSerialPort()
    const transport = new WebSerialRadioTransport(port, {
      baudRate: profile.baudRate,
      parity:
        'parity' in profile && profile.parity === 'even' ? 'even' : 'none',
      stopBits:
        'stopBits' in profile && profile.stopBits === 2 ? 2 : 1,
    })
    transportRef.current = transport
    return transport
  }

  async function openTransportForSignals(
    transport: ProgrammerTransport,
    signals: (typeof SIGNAL_MODES)[number],
    label: string,
  ): Promise<void> {
    if (!(transport instanceof WebSerialRadioTransport)) {
      await transport.open()
      return
    }
    setProgress({ current: 0, total: 1, label })
    await transport.close()
    await new Promise((resolve) => setTimeout(resolve, 120))
    setProgress({
      current: 0,
      total: 1,
      label: 'Pulsing DTR/RTS and opening a fresh serial session',
    })
    await transport.open({ signals, pulseSignals: true })
  }

  async function prepareFreshRadioSession(
    transport: ProgrammerTransport,
    label: string,
  ): Promise<void> {
    setProgress({ current: 0, total: 1, label })
    await transport.close()
    await new Promise((resolve) => setTimeout(resolve, 900))
    setProgress({ current: 0, total: 1, label: 'Waiting for radio to reboot' })
    if (transport instanceof WebSerialRadioTransport) {
      await transport.open({
        signals: signalModeRef.current,
        pulseSignals: true,
      })
    } else {
      await transport.open()
    }
    await new Promise((resolve) => setTimeout(resolve, 5000))
  }

  async function connectAndRead(): Promise<void> {
    trackProgrammerEvent('connect_attempt', profile.model)
    setBusy('reading')
    setError('')
    setNotice('')
    setProgress({ current: 0, total: 1, label: 'Opening serial port' })
    try {
      const transport = await ensureTransport()
      let result: Awaited<ReturnType<typeof browserReadRadio>> | null = null
      let lastError: unknown = null
      if (transport instanceof WebHidRadioTransport) {
        await transport.open()
        try {
          result = await browserReadRadio(profile, transport, setProgress)
        } catch (cause) {
          lastError = cause
          await transport.discardInput()
        }
      } else {
        for (const [index, signals] of SIGNAL_MODES.entries()) {
          await openTransportForSignals(
            transport,
            signals,
            `Trying DTR/RTS mode ${index + 1} of ${SIGNAL_MODES.length}`,
          )
          try {
            result = await browserReadRadio(profile, transport, setProgress)
            signalModeRef.current = signals
            break
          } catch (cause) {
            lastError = cause
            await transport.discardInput()
          }
        }
      }
      if (!result) {
        const detail =
          lastError instanceof Error
            ? lastError.message
            : `Unable to read ${profile.vendor} ${profile.model}.`
        throw new Error(
          `${detail} ${
            transport instanceof WebHidRadioTransport
              ? 'The USB HID session could not be established.'
              : `NewChirp tried all ${SIGNAL_MODES.length} DTR/RTS combinations with a fresh serial session.`
          }`,
        )
      }
      setImage(result.image)
      setBaseline(result.image.slice())
      setChannels(browserParseChannels(result.image, profile))
      setReportedModel(result.reportedModel)
      setConnected(true)
      setSelected(null)
      trackProgrammerEvent('read_success', profile.model)
      setSuccessfulReads((current) => (current ?? 0) + 1)
      setNotice(
        'Radio downloaded successfully. Save the unedited backup before making changes.',
      )
    } catch (cause) {
      trackProgrammerEvent('read_failure', profile.model)
      setError(messageFor(cause))
      await transportRef.current?.close()
      transportRef.current = null
      setConnected(false)
    } finally {
      setBusy('')
    }
  }

  async function uploadAndVerify(): Promise<void> {
    if (!image) return
    if (profileReadOnly) {
      setError(
        'Radio writes remain disabled for this TID HID beta until physical hardware validation is recorded.',
      )
      return
    }
    if (!window.confirm(
      `Write the edited image to ${profile.vendor} ${profile.model}?\n\nNewChirp will read the radio back immediately and compare the complete image.`,
    )) return

    trackProgrammerEvent('write_attempt', profile.model)
    setBusy('writing')
    setError('')
    setNotice('')
    try {
      const transport = await ensureTransport()
      await prepareFreshRadioSession(transport, 'Restarting radio session')
      await browserWriteRadio(profile, image, transport, setProgress)
      setBusy('verifying')
      await prepareFreshRadioSession(
        transport,
        'Restarting radio for verification',
      )
      const verification = await browserReadRadio(
        profile,
        transport,
        setProgress,
      )
      const difference = browserCompareImages(
        profile,
        image,
        verification.image,
      )
      if (difference !== null) {
        throw new Error(
          `Read-back did not match at image offset 0x${difference.toString(16)}. Do not make further changes; restore your backup.`,
        )
      }
      setImage(verification.image)
      setBaseline(verification.image.slice())
      setChannels(browserParseChannels(verification.image, profile))
      setReportedModel(verification.reportedModel)
      setConnected(true)
      trackProgrammerEvent('write_success', profile.model)
      setNotice('Upload complete. Full-image read-back matched byte for byte.')
    } catch (cause) {
      trackProgrammerEvent('write_failure', profile.model)
      setError(messageFor(cause))
    } finally {
      setBusy('')
    }
  }

  function downloadBytes(
    bytes: Uint8Array,
    suffix: string,
    extension = 'img',
  ): void {
    const blob = new Blob([bytes], { type: 'application/octet-stream' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    const date = new Date().toISOString().slice(0, 10)
    link.href = url
    link.download = `NewChirp-${safeFilename(profile.model)}-${date}-${suffix}.${extension}`
    link.click()
    URL.revokeObjectURL(url)
  }

  function saveBackup(): void {
    if (!baseline) return
    const portableCat =
      profile.protocol === 'kenwood-cat' || profile.protocol === 'icom-civ'
    const tidCps = profile.protocol === 'tid-hid'
    downloadBytes(
      browserCreateBackup(profile, baseline),
      'backup',
      tidCps ? 'td' : portableCat ? 'bin' : 'img',
    )
    setNotice(
      tidCps
        ? 'Unedited TID CPS-compatible .td backup saved locally.'
        : portableCat
        ? 'Unedited NewChirp backup saved locally. This live-CAT radio does not expose a raw CHIRP image.'
        : 'Unedited CHIRP-compatible backup saved locally.',
    )
  }

  function exportCurrent(): void {
    if (!image) return
    const portableCat =
      profile.protocol === 'kenwood-cat' || profile.protocol === 'icom-civ'
    const tidCps = profile.protocol === 'tid-hid'
    downloadBytes(
      browserCreateBackup(profile, image),
      'edited',
      tidCps ? 'td' : portableCat ? 'bin' : 'img',
    )
    setNotice(
      tidCps
        ? 'Edited TID CPS-compatible .td codeplug saved locally.'
        : portableCat
        ? 'Current edited NewChirp CAT backup saved locally.'
        : 'Current edited image saved locally.',
    )
  }

  async function importImage(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    setError('')
    try {
      const bytes = new Uint8Array(await file.arrayBuffer())
      const imported = browserExtractImage(profile, bytes)
      setImage(imported)
      setBaseline(imported.slice())
      setChannels(browserParseChannels(imported, profile))
      setSelected(null)
      setConnected(false)
      setReportedModel('')
      setNotice(`${file.name} loaded for offline editing. Connect the radio before uploading.`)
    } catch (cause) {
      setError(messageFor(cause))
    }
  }

  async function changeProfile(nextId: string): Promise<void> {
    if (changed && !window.confirm('Discard the current edited image?')) return
    await transportRef.current?.close()
    transportRef.current = null
    setProfileId(nextId)
    try {
      window.localStorage.setItem(PROFILE_STORAGE_KEY, nextId)
    } catch {
      // Selection still works for the current session without storage.
    }
    setRadioQuery('')
    clearRadioState()
  }

  function closeEditor(): void {
    setPlacementOpen(false)
    setNewChannelTarget(null)
    setEditor(null)
  }

  function openNewChannelAt(target: number): void {
    const template =
      channels.find((channel) => channel.empty) ?? channels[0] ?? null
    if (!template) {
      setError('No empty channel slots are available.')
      return
    }
    const frequency =
      profile.protocol === 'rt22' ||
      profile.protocol === 't18' ||
      profile.protocol === 'rb15' ||
      profile.protocol === 'mxt295'
        ? 462_562_500
        : 146_520_000
    setNewChannelTarget(target)
    setPlacementOpen(false)
    setEditor({
      ...template,
      number: target,
      empty: false,
      name:
        profile.protocol === 'mxt295'
          ? 'NEW CHAN'
          : profile.nameLength > 0
            ? 'NEW CHANNEL'
            : '',
      rxFrequency: frequency,
      txFrequency: frequency,
      toneMode: 'none',
      ctcss: CTCSS_TONES[0],
      dtcs: DTCS_CODES[0],
      dtcsPolarity: 'N',
      mode: 'FM',
      power: profile.powerLevels[0] ?? 'High',
      skip: false,
    })
  }

  function saveEditor(): void {
    if (!editor || !image || !editor.rxFrequency) return
    const inserting =
      newChannelTarget !== null && editor.number === newChannelTarget
    let next = image.slice()
    try {
      if (inserting) {
        next = browserInsertChannel(next, profile, channels, editor)
      } else {
        browserApplyChannel(next, profile, editor)
      }
    } catch (cause) {
      setError(messageFor(cause))
      return
    }
    const refreshed = browserParseChannels(next, profile)
    const updated = refreshed.find(
      (channel) => channel.number === editor.number,
    )
    if (!updated) return
    setImage(next)
    setChannels(refreshed)
    setSelected(updated.number)
    closeEditor()
    setNotice(
      inserting
        ? `Channel added at ${browserChannelLabel(profile, updated.number)}.`
        : `Channel ${browserChannelLabel(profile, updated.number)} updated in the browser image.`,
    )
  }

  function clearChannel(channel: WebChannel): void {
    if (!image) return
    if (
      !window.confirm(
        `Clear channel ${browserChannelLabel(profile, channel.number)}?`,
      )
    )
      return
    const next = image.slice()
    try {
      browserApplyChannel(next, profile, { ...channel, empty: true })
    } catch (cause) {
      setError(messageFor(cause))
      return
    }
    const updated = browserParseChannels(next, profile).find(
      (candidate) => candidate.number === channel.number,
    )
    if (!updated) return
    setImage(next)
    setChannels((current) =>
      current.map((candidate) =>
        candidate.number === updated.number ? updated : candidate,
      ),
    )
    setSelected(null)
    setNewChannelTarget(null)
  }

  function moveChannel(fromNumber: number, toNumber: number): void {
    if (!image || disabled || query.trim() || fromNumber === toNumber) return
    try {
      const next = browserMoveChannel(
        image,
        profile,
        channels,
        fromNumber,
        toNumber,
      )
      const refreshed = browserParseChannels(next, profile)
      setImage(next)
      setChannels(refreshed)
      setSelected(toNumber)
      setNotice(
        `Moved channel ${browserChannelLabel(profile, fromNumber)} to ${browserChannelLabel(profile, toNumber)}.`,
      )
      setError('')
    } catch (cause) {
      setError(messageFor(cause))
    } finally {
      setDraggedChannel(null)
    }
  }

  const disabled = Boolean(busy)
  const canInsertAround =
    selected !== null &&
    profile.protocol !== 'mxt295' &&
    profile.protocol !== 'baofeng-dm32uv'
  const visibleEditorExtras =
    editor?.extras?.filter(
      (extra) =>
        !extra.visibleModes || extra.visibleModes.includes(editor.mode),
    ) ?? []
  const endInsertionTarget =
    image && channels.length > 0
      ? browserInsertionTarget(channels, 'end', selected)
      : null
  const beforeInsertionTarget =
    image && channels.length > 0 && canInsertAround
      ? browserInsertionTarget(channels, 'before-selected', selected)
      : null
  const afterInsertionTarget =
    image && channels.length > 0 && canInsertAround
      ? browserInsertionTarget(channels, 'after-selected', selected)
      : null

  return (
    <section className="relative overflow-hidden border-b border-border/60">
      <div className="grid-bg absolute inset-0 opacity-55" aria-hidden />
      <div
        className="pointer-events-none absolute -left-32 top-10 h-80 w-80 rounded-full bg-primary/15 blur-3xl"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -right-32 top-48 h-80 w-80 rounded-full bg-[#9b5b18]/10 blur-3xl"
        aria-hidden
      />

      <div className="relative mx-auto max-w-7xl px-4 py-10 md:py-14">
        <div className="grid items-start gap-8 lg:grid-cols-[1.05fr_0.95fr]">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-[#9b5b18]/30 bg-[#9b5b18]/10 px-3 py-1 font-mono text-xs font-semibold uppercase tracking-wide text-[#8a4e12]">
              <span className="h-2 w-2 rounded-full bg-[#c46a15]" />
              Web Programmer · Beta
            </span>
            <h1 className="mt-5 max-w-3xl text-balance text-4xl font-bold tracking-tight sm:text-5xl">
              Program your radio{' '}
              <span className="text-primary">right from the browser.</span>
            </h1>
            <p className="mt-5 max-w-2xl text-pretty text-lg leading-relaxed text-muted-foreground">
              Connect the programming cable, grant access to the{' '}
              {profileUsesHid ? 'USB HID device' : 'serial port'}, and
              read or write supported radios without installing anything. Radio
              data stays in this browser.
            </p>
            <div className="mt-6 flex flex-wrap gap-2 text-xs text-muted-foreground">
              <span className="rounded-full border border-border bg-card px-3 py-1.5">
                Web Serial radios: Chrome, Edge, or Firefox 151+
              </span>
              <span className="rounded-full border border-border bg-card px-3 py-1.5">
                WebHID radios: desktop Chrome or Edge only
              </span>
              <span className="rounded-full border border-border bg-card px-3 py-1.5">
                Local-only radio data
              </span>
            </div>
            <div
              className="mt-8 inline-flex min-w-64 items-center gap-4 rounded-2xl border border-primary/20 bg-card/90 px-5 py-4 shadow-lg shadow-primary/5"
              aria-live="polite"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                <CheckCircle2 className="h-5 w-5" />
              </span>
              <div>
                <p className="font-mono text-3xl font-bold tracking-tight text-foreground">
                  {successfulReads === null
                    ? '—'
                    : successfulReads.toLocaleString()}
                </p>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  successful radio reads made so far
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-card p-5 shadow-xl shadow-primary/5 md:p-6">
            <div className="flex items-start gap-3">
              <div className="rounded-xl bg-secondary p-2.5 text-primary">
                <RadioTower className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-lg font-bold">Choose your radio</h2>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                  Select the exact model printed on the radio. The UV‑5R Mini is
                  the first recommended test radio.
                </p>
              </div>
            </div>

            <label className="mt-5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {BROWSER_RADIO_PROFILES.length} browser-ready radios
            </label>
            <details
              ref={profilePickerRef}
              className="relative mt-2"
              onToggle={(event) => {
                if (event.currentTarget.open) {
                  requestAnimationFrame(() => radioSearchRef.current?.focus())
                }
              }}
            >
              <summary
                aria-disabled={disabled || Boolean(image)}
                onClick={(event) => {
                  if (disabled || image) event.preventDefault()
                }}
                className="flex h-11 w-full cursor-pointer list-none items-center justify-between gap-3 rounded-lg border border-border bg-background px-3 text-sm outline-none transition-colors hover:border-primary/40 focus-visible:ring-2 focus-visible:ring-primary/20 [&::-webkit-details-marker]:hidden"
              >
                <span className="truncate">
                  {profile.vendor} {profile.model}
                </span>
                <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
              </summary>
              <div className="absolute z-40 mt-2 w-full overflow-hidden rounded-xl border border-border bg-card shadow-2xl shadow-black/15">
                <div className="border-b border-border p-2">
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <input
                      ref={radioSearchRef}
                      type="search"
                      value={radioQuery}
                      onChange={(event) => setRadioQuery(event.target.value)}
                      placeholder="Find manufacturer or model"
                      className="h-9 w-full rounded-lg border border-border bg-background pl-9 pr-3 text-sm outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                </div>
                <div className="max-h-80 overflow-y-auto p-2">
                  {filteredRadioGroups.map((group) => (
                    <div key={group.label} className="mb-3 last:mb-0">
                      <div className="-mx-2 mb-1 flex items-center gap-2 border-y border-primary/20 bg-[#e4ede7] px-3 py-2.5">
                        <span
                          className="h-4 w-1 shrink-0 rounded-full bg-primary"
                          aria-hidden
                        />
                        <p className="text-xs font-bold uppercase tracking-[0.08em] text-[#173f31]">
                          {group.label}
                        </p>
                        <span className="ml-auto rounded-full bg-card px-2 py-0.5 font-mono text-[10px] font-bold text-primary ring-1 ring-primary/15">
                          {group.profiles.length}
                        </span>
                      </div>
                      <div className="space-y-0.5 px-1">
                        {group.profiles.map((candidate) => (
                          <button
                            key={candidate.id}
                            type="button"
                            onClick={() => {
                              profilePickerRef.current?.removeAttribute('open')
                              void changeProfile(candidate.id)
                            }}
                            className={`flex w-full items-center justify-between gap-3 rounded-md px-2 py-2 text-left text-sm transition-colors hover:bg-secondary ${
                              candidate.id === profileId
                                ? 'bg-primary/10 font-semibold text-primary'
                                : ''
                            }`}
                          >
                            <span className="min-w-0">
                              <span className="text-muted-foreground">
                                {candidate.vendor}
                              </span>{' '}
                              {candidate.model}
                            </span>
                            <span className="flex shrink-0 items-center gap-2">
                              <span
                                className={`rounded-full border px-2 py-0.5 font-mono text-[9px] font-semibold ${
                                  candidate.protocol === 'tid-hid'
                                    ? 'border-[#9b5b18]/35 bg-[#9b5b18]/10 text-[#8a4e12]'
                                    : 'border-primary/25 bg-primary/5 text-primary'
                                }`}
                              >
                                {candidate.protocol === 'tid-hid'
                                  ? 'HID · Chrome/Edge desktop'
                                  : 'Serial · Chrome/Edge/Firefox 151+'}
                              </span>
                              {candidate.id === profileId && (
                                <Check className="h-4 w-4" />
                              )}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                  {filteredRadioGroups.length === 0 && (
                    <p className="px-3 py-8 text-center text-sm text-muted-foreground">
                      No matching radio models.
                    </p>
                  )}
                </div>
              </div>
            </details>
            <p className="mt-2 text-sm text-muted-foreground">{profile.summary}</p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {profile.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded bg-secondary px-2 py-1 font-mono text-[10px] font-semibold uppercase tracking-wide text-secondary-foreground"
                >
                  {tag}
                </span>
              ))}
            </div>
            {profileUsesHid && (
              <div
                className={`mt-4 rounded-xl border p-4 ${
                  hidAvailable
                    ? 'border-primary/30 bg-primary/5'
                    : 'border-[#9b5b18]/35 bg-[#9b5b18]/10'
                }`}
              >
                <div className="flex items-start gap-3">
                  {hidAvailable ? (
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                  ) : (
                    <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-[#9b5b18]" />
                  )}
                  <div>
                    <p className="font-semibold text-foreground">
                      USB HID radio: desktop Chrome or Edge required
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                      This TIDRADIO profile uses WebHID. Firefox does not support
                      WebHID, and Safari or mobile browsers cannot program this radio
                      family. {hidAvailable
                        ? 'This browser supports WebHID and is ready to connect.'
                        : 'Open this page in desktop Chrome or Edge to continue.'}
                    </p>
                  </div>
                </div>
              </div>
            )}
            {profileReadOnly && (
              <div className="mt-4 rounded-lg border border-[#9b5b18]/25 bg-[#9b5b18]/5 p-3 text-sm text-muted-foreground">
                <span className="font-semibold text-foreground">Read-only beta.</span>{' '}
                This CPS-derived TID profile can download and back up the radio, and
                its codeplug can be edited offline. Radio writes stay disabled until
                the first physical read/edit/upload/read-back validation is recorded.
              </div>
            )}

            {!profileUsesHid && !serialAvailable && (
              <div className="mt-5 flex gap-3 rounded-lg border border-[#9b5b18]/25 bg-[#9b5b18]/5 p-3 text-sm">
                <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-[#9b5b18]" />
                <p>
                  This browser does not expose Web Serial. Open this page in
                  Chrome, Edge, or Firefox 151 or newer. Safari and mobile browsers
                  should use the desktop app for programming.
                </p>
              </div>
            )}

            <div className="mt-5 grid gap-2 sm:grid-cols-2">
              <Button
                className="web-programmer-halo"
                size="lg"
                onClick={() => void connectAndRead()}
                disabled={
                  disabled ||
                  (profileUsesHid ? !hidAvailable : !serialAvailable)
                }
              >
                <PlugZap className="h-4 w-4" />
                {profileUsesHid
                  ? 'Connect USB HID & read radio'
                  : 'Connect & read radio'}
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled}
              >
                <FileUp className="h-4 w-4" />
                Open image
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".img,.bin,.td,application/octet-stream"
                className="hidden"
                onChange={(event) => void importImage(event)}
              />
            </div>

            {busy && (
              <div className="mt-5 rounded-lg border border-primary/20 bg-secondary/60 p-3">
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="font-medium">{progress.label}</span>
                  <span className="font-mono text-xs">{progressPercent}%</span>
                </div>
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-background">
                  <div
                    className="h-full rounded-full bg-primary transition-[width]"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="mt-6 flex items-start gap-3 rounded-xl border border-destructive/25 bg-destructive/5 p-4 text-sm">
            <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
            <div>
              <p className="font-semibold text-destructive">Radio operation failed</p>
              <p className="mt-1 leading-relaxed text-muted-foreground">{error}</p>
            </div>
          </div>
        )}

        {notice && (
          <div className="mt-6 flex items-start gap-3 rounded-xl border border-primary/25 bg-secondary/60 p-4 text-sm">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
            <p className="leading-relaxed">{notice}</p>
          </div>
        )}

        {image && (
          <div className="mt-8 overflow-hidden rounded-2xl border border-border bg-card shadow-xl shadow-primary/5">
            <div className="border-b border-border bg-[#f7faf8] px-4 py-4 md:px-5">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-xl font-bold">
                      {profile.vendor} {profile.model}
                    </h2>
                    <span
                      className={`rounded-full px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-wide ${
                        connected
                          ? 'bg-primary/10 text-primary'
                          : 'bg-[#9b5b18]/10 text-[#8a4e12]'
                      }`}
                    >
                      {connected ? 'Radio connected' : 'Offline image'}
                    </span>
                    {changed && (
                      <span className="rounded-full bg-[#9b5b18]/10 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-wide text-[#8a4e12]">
                        Unsaved changes
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {populatedCount} populated channels
                    {reportedModel ? ` · Radio reports ${reportedModel}` : ''}
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={saveBackup}
                    disabled={!baseline || disabled}
                  >
                    <ShieldCheck className="h-3.5 w-3.5" />
                    Save backup
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={exportCurrent}
                    disabled={disabled}
                  >
                    <FileDown className="h-3.5 w-3.5" />
                    Export edited
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => void uploadAndVerify()}
                    disabled={disabled || !changed || profileReadOnly}
                  >
                    <Upload className="h-3.5 w-3.5" />
                    Upload &amp; verify
                  </Button>
                  {connected && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => void disconnect()}
                      disabled={disabled}
                    >
                      <Unplug className="h-3.5 w-3.5" />
                      Close port
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => void discardImage()}
                    disabled={disabled}
                  >
                    <X className="h-3.5 w-3.5" />
                    Close image
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3 border-b border-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between md:px-5">
              <div className="relative w-full sm:max-w-sm">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search channel, name, or frequency"
                  className="h-9 w-full rounded-lg border border-border bg-background pl-9 pr-3 text-sm outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20"
                />
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-sm text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={showEmpty}
                    onChange={(event) => setShowEmpty(event.target.checked)}
                    className="h-4 w-4 accent-[var(--primary)]"
                  />
                  Show empty slots
                </label>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setPlacementOpen(true)}
                  disabled={disabled}
                >
                  <Pencil className="h-3.5 w-3.5" />
                  New channel
                </Button>
              </div>
            </div>

            <div className="max-h-[580px] overflow-auto">
              <table className="w-full min-w-[850px] border-collapse text-left text-sm">
                <thead className="sticky top-0 z-10 bg-[#edf3ef] text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="w-20 px-4 py-3 font-semibold">CH</th>
                    <th className="px-4 py-3 font-semibold">Name</th>
                    <th className="px-4 py-3 font-semibold">RX MHz</th>
                    <th className="px-4 py-3 font-semibold">TX MHz</th>
                    <th className="px-4 py-3 font-semibold">Tone</th>
                    <th className="px-4 py-3 font-semibold">Mode</th>
                    <th className="px-4 py-3 font-semibold">Power</th>
                    <th className="w-20 px-4 py-3 font-semibold">Scan</th>
                    <th className="w-24 px-4 py-3 font-semibold">Edit</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredChannels.map((channel, index, visibleChannels) => {
                    const previous = visibleChannels[index - 1]
                    const next = visibleChannels[index + 1]
                    const startsEmptyGroup =
                      channel.empty && (!previous || !previous.empty)
                    const endsEmptyGroup =
                      channel.empty && (!next || !next.empty)
                    const canDrag =
                      !channel.empty &&
                      !disabled &&
                      query.trim() === '' &&
                      profile.protocol !== 'mxt295' &&
                      profile.protocol !== 'baofeng-dm32uv'
                    return (
                      <tr
                        key={channel.number}
                        draggable={canDrag}
                        onDragStart={(event) => {
                          if (!canDrag) {
                            event.preventDefault()
                            return
                          }
                          event.dataTransfer.effectAllowed = 'move'
                          event.dataTransfer.setData(
                            'text/plain',
                            String(channel.number),
                          )
                          setDraggedChannel(channel.number)
                        }}
                        onDragOver={(event) => {
                          if (
                            draggedChannel !== null &&
                            draggedChannel !== channel.number &&
                            query.trim() === ''
                          ) {
                            event.preventDefault()
                            event.dataTransfer.dropEffect = 'move'
                          }
                        }}
                        onDrop={(event) => {
                          event.preventDefault()
                          const from = Number(
                            event.dataTransfer.getData('text/plain') ||
                              draggedChannel,
                          )
                          if (Number.isFinite(from)) {
                            moveChannel(from, channel.number)
                          }
                        }}
                        onDragEnd={() => setDraggedChannel(null)}
                        onClick={() => setSelected(channel.number)}
                        aria-selected={selected === channel.number}
                        className={`border-t border-border/70 transition-colors ${
                          canDrag ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'
                        } ${
                          startsEmptyGroup ? 'border-t-2 border-t-[#c8d4ce]' : ''
                        } ${endsEmptyGroup ? 'border-b-2 border-b-[#c8d4ce]' : ''} ${
                          selected === channel.number
                            ? 'bg-secondary'
                            : channel.empty
                              ? 'bg-[#fafafa] text-muted-foreground'
                              : 'bg-card hover:bg-secondary/40'
                        }`}
                      >
                      <td className="px-4 py-2.5 font-mono font-semibold">
                        <span className="flex items-center gap-1.5">
                          {canDrag ? (
                            <GripVertical
                              className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                              aria-hidden="true"
                            />
                          ) : (
                            <span className="w-3.5 shrink-0" aria-hidden="true" />
                          )}
                          {browserChannelLabel(profile, channel.number)}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 font-semibold">
                        {channel.name || (channel.empty ? 'Empty' : '—')}
                      </td>
                      <td className="px-4 py-2.5 font-mono">
                        {formatFrequency(channel.rxFrequency)}
                      </td>
                      <td className="px-4 py-2.5 font-mono">
                        {formatFrequency(channel.txFrequency)}
                      </td>
                      <td className="px-4 py-2.5">
                        {channel.toneMode === 'none'
                          ? 'None'
                          : channel.toneMode === 'ctcss'
                            ? `${channel.ctcss?.toFixed(1)} Hz`
                            : `D${channel.dtcs}${channel.dtcsPolarity}`}
                      </td>
                      <td className="px-4 py-2.5">{channel.mode}</td>
                      <td className="px-4 py-2.5">{channel.power}</td>
                      <td className="px-4 py-2.5">
                        {profile.protocol === 'baofeng-dm32uv'
                          ? '—'
                          : channel.skip
                            ? 'Skip'
                            : 'Include'}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            className="rounded p-1.5 text-primary hover:bg-primary/10"
                            aria-label={`Edit channel ${browserChannelLabel(profile, channel.number)}`}
                            onClick={(event) => {
                              event.stopPropagation()
                              setNewChannelTarget(null)
                              setEditor(editableChannel(channel))
                            }}
                            disabled={disabled}
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          {!channel.empty && (
                            <button
                              type="button"
                              className="rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                              aria-label={`Clear channel ${browserChannelLabel(profile, channel.number)}`}
                              onClick={(event) => {
                                event.stopPropagation()
                                clearChannel(channel)
                              }}
                              disabled={disabled}
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              {filteredChannels.length === 0 && (
                <div className="px-6 py-12 text-center text-sm text-muted-foreground">
                  No channels match this filter.
                </div>
              )}
            </div>

            <div className="flex flex-col gap-2 border-t border-border bg-[#f7faf8] px-4 py-3 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between md:px-5">
              <span>
                Browser names use the radio name field with ASCII-safe editing in
                this beta.
              </span>
              <span className="inline-flex items-center gap-1.5 font-mono">
                <ShieldCheck className="h-3.5 w-3.5 text-primary" />
                Full-image read-back verification required
              </span>
            </div>
          </div>
        )}

        <div className="mt-8 grid gap-4 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="rounded-xl border border-border bg-card p-5">
            <div className="flex items-center gap-2">
              <Cable className="h-5 w-5 text-primary" />
              <h2 className="font-bold">Before you connect</h2>
            </div>
            <ol className="mt-4 grid gap-3 text-sm text-muted-foreground sm:grid-cols-2">
              {[
                'Close CHIRP, the vendor CPS, and any other serial software.',
                'Connect the programming cable and power on the radio.',
                'Select the exact radio model above.',
                'Use Firefox 151+ on macOS, or Chrome/Edge on macOS, Windows, or Linux.',
              ].map((step, index) => (
                <li key={step} className="flex gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-secondary font-mono text-xs font-bold text-primary">
                    {index + 1}
                  </span>
                  <span className="leading-relaxed">{step}</span>
                </li>
              ))}
            </ol>
            <div className="mt-4 flex gap-3 rounded-lg border border-border bg-background p-3 text-sm text-muted-foreground">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <p>
                The browser talks directly to the USB cable. Channel data is never
                uploaded to NewChirp servers.
              </p>
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-5">
            <h2 className="font-bold">More browser radios are coming</h2>
            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
              A radio may already work in the desktop app while its direct browser
              driver is still being developed. We claim browser support only after a
              physical read-back works.
            </p>
            <div className="mt-4 divide-y divide-border">
              {roadmapRadios.map((radio) => (
                <div
                  key={`${radio.vendor}-${radio.model}`}
                  className="flex items-center justify-between gap-3 py-2.5 text-sm"
                >
                  <span>
                    <span className="text-muted-foreground">{radio.vendor}</span>{' '}
                    <span className="font-semibold">{radio.model}</span>
                  </span>
                  <span className="text-right text-xs text-muted-foreground">
                    {radio.note}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {placementOpen && image && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/35 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="web-add-channel-placement-title"
        >
          <div className="w-full max-w-md rounded-2xl border border-border bg-card p-5 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-mono text-xs font-semibold uppercase tracking-wide text-primary">
                  Channel placement
                </p>
                <h2
                  id="web-add-channel-placement-title"
                  className="mt-1 text-2xl font-bold"
                >
                  Where should the new channel go?
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  {selected === null
                    ? 'Select a channel to insert before or after it.'
                    : `Selected: ${browserChannelLabel(profile, selected)}`}
                </p>
              </div>
              <Button
                size="icon-sm"
                variant="ghost"
                aria-label="Cancel new channel"
                onClick={() => setPlacementOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="mt-5 grid gap-2">
              <Button
                variant="outline"
                className="h-auto justify-between py-3"
                disabled={endInsertionTarget === null}
                onClick={() => {
                  if (endInsertionTarget !== null) {
                    openNewChannelAt(endInsertionTarget)
                  }
                }}
              >
                <span>At the end</span>
                <span className="font-mono text-xs text-muted-foreground">
                  {endInsertionTarget === null
                    ? 'No free slot'
                    : browserChannelLabel(profile, endInsertionTarget)}
                </span>
              </Button>
              <Button
                variant="outline"
                className="h-auto justify-between py-3"
                disabled={beforeInsertionTarget === null}
                onClick={() => {
                  if (beforeInsertionTarget !== null) {
                    openNewChannelAt(beforeInsertionTarget)
                  }
                }}
              >
                <span>Before the selected channel</span>
                <span className="text-xs text-muted-foreground">Shift down</span>
              </Button>
              <Button
                variant="outline"
                className="h-auto justify-between py-3"
                disabled={afterInsertionTarget === null}
                onClick={() => {
                  if (afterInsertionTarget !== null) {
                    openNewChannelAt(afterInsertionTarget)
                  }
                }}
              >
                <span>After the selected channel</span>
                <span className="text-xs text-muted-foreground">Shift down</span>
              </Button>
            </div>
          </div>
        </div>
      )}

      {editor && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/35 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="web-channel-editor-title"
        >
          <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-border bg-card p-5 shadow-2xl md:p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-mono text-xs font-semibold uppercase tracking-wide text-primary">
                  Channel {browserChannelLabel(profile, editor.number)}
                </p>
                <h2 id="web-channel-editor-title" className="mt-1 text-2xl font-bold">
                  Edit channel
                </h2>
              </div>
              <Button
                size="icon-sm"
                variant="ghost"
                aria-label="Close channel editor"
                onClick={closeEditor}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              {profile.nameLength > 0 && (
                <label className="sm:col-span-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Channel name
                  </span>
                  <input
                    value={editor.name}
                    maxLength={profile.nameLength}
                    onChange={(event) =>
                      setEditor({ ...editor, name: event.target.value })
                    }
                    className="mt-1.5 h-10 w-full rounded-lg border border-border bg-background px-3 text-sm outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20"
                  />
                  <span className="mt-1 block text-xs text-muted-foreground">
                    ASCII characters only in the browser beta, up to {profile.nameLength}.
                  </span>
                </label>
              )}

              <label>
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  RX frequency (MHz)
                </span>
                <input
                  inputMode="decimal"
                  defaultValue={
                    editor.rxFrequency
                      ? (editor.rxFrequency / 1_000_000).toFixed(6)
                      : ''
                  }
                  onChange={(event) =>
                    setEditor({
                      ...editor,
                      rxFrequency: parseFrequency(event.target.value),
                    })
                  }
                  className="mt-1.5 h-10 w-full rounded-lg border border-border bg-background px-3 font-mono text-sm outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20"
                />
              </label>

              <label>
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  TX frequency (MHz)
                </span>
                <input
                  inputMode="decimal"
                  defaultValue={
                    editor.txFrequency
                      ? (editor.txFrequency / 1_000_000).toFixed(6)
                      : ''
                  }
                  onChange={(event) =>
                    setEditor({
                      ...editor,
                      txFrequency: parseFrequency(event.target.value),
                    })
                  }
                  className="mt-1.5 h-10 w-full rounded-lg border border-border bg-background px-3 font-mono text-sm outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20"
                />
              </label>

              {editor.mode !== 'DMR' && (
                <label>
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Tone mode
                  </span>
                  <select
                    value={editor.toneMode}
                    onChange={(event) =>
                      setEditor({
                        ...editor,
                        toneMode: event.target.value as WebChannel['toneMode'],
                      })
                    }
                    className="mt-1.5 h-10 w-full rounded-lg border border-border bg-background px-3 text-sm outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20"
                  >
                    <option value="none">None</option>
                    <option value="ctcss">CTCSS</option>
                    <option value="dtcs">DTCS</option>
                  </select>
                </label>
              )}

              {editor.mode !== 'DMR' && editor.toneMode === 'ctcss' && (
                <label>
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    CTCSS tone
                  </span>
                  <select
                    value={editor.ctcss ?? CTCSS_TONES[0]}
                    onChange={(event) =>
                      setEditor({ ...editor, ctcss: Number(event.target.value) })
                    }
                    className="mt-1.5 h-10 w-full rounded-lg border border-border bg-background px-3 text-sm outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20"
                  >
                    {CTCSS_TONES.map((tone) => (
                      <option key={tone} value={tone}>
                        {tone.toFixed(1)} Hz
                      </option>
                    ))}
                  </select>
                </label>
              )}

              {editor.mode !== 'DMR' && editor.toneMode === 'dtcs' && (
                <>
                  <label>
                    <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      DTCS code
                    </span>
                    <select
                      value={editor.dtcs ?? DTCS_CODES[0]}
                      onChange={(event) =>
                        setEditor({ ...editor, dtcs: Number(event.target.value) })
                      }
                      className="mt-1.5 h-10 w-full rounded-lg border border-border bg-background px-3 text-sm outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20"
                    >
                      {DTCS_CODES.map((code) => (
                        <option key={code} value={code}>
                          D{code.toString().padStart(3, '0')}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Polarity
                    </span>
                    <select
                      value={editor.dtcsPolarity}
                      onChange={(event) =>
                        setEditor({
                          ...editor,
                          dtcsPolarity: event.target.value as 'N' | 'R',
                        })
                      }
                      className="mt-1.5 h-10 w-full rounded-lg border border-border bg-background px-3 text-sm outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20"
                    >
                      <option value="N">Normal</option>
                      <option value="R">Reverse</option>
                    </select>
                  </label>
                </>
              )}

              <label>
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Bandwidth
                </span>
                <select
                  value={editor.mode}
                  onChange={(event) =>
                    setEditor({
                      ...editor,
                      mode: event.target.value as WebChannel['mode'],
                    })
                  }
                  className="mt-1.5 h-10 w-full rounded-lg border border-border bg-background px-3 text-sm outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20"
                >
                  {((profile.protocol === 'baofeng-dm32uv'
                    ? profile.modes
                    : 'modes' in profile
                      ? profile.modes
                      : profile.protocol === 'ft60' ||
                          (profile.protocol === 'uv5r' && profile.amBand)
                        ? ['FM', 'NFM', 'AM']
                        : ['FM', 'NFM']) as readonly string[]
                  ).map((mode) => (
                    <option key={mode} value={mode}>
                      {mode === 'FM'
                        ? 'Wide FM'
                        : mode === 'NFM'
                          ? 'Narrow FM'
                          : mode}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Power
                </span>
                <select
                  value={editor.power}
                  onChange={(event) =>
                    setEditor({ ...editor, power: event.target.value })
                  }
                  className="mt-1.5 h-10 w-full rounded-lg border border-border bg-background px-3 text-sm outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20"
                >
                  {profile.powerLevels.map((power) => (
                    <option key={power} value={power}>
                      {power}
                    </option>
                  ))}
                </select>
              </label>

              {profile.protocol !== 'baofeng-dm32uv' && (
                <label className="flex items-center gap-3 rounded-lg border border-border px-3 py-2.5 sm:col-span-2">
                  <input
                    type="checkbox"
                    checked={!editor.skip}
                    onChange={(event) =>
                      setEditor({ ...editor, skip: !event.target.checked })
                    }
                    className="h-4 w-4 accent-[var(--primary)]"
                  />
                  <span className="text-sm">Include this channel when scanning</span>
                </label>
              )}

              {visibleEditorExtras.length > 0 && (
                <div className="sm:col-span-2">
                  <div className="border-t border-border pt-5">
                    <h3 className="text-sm font-bold">Radio-specific channel settings</h3>
                    <p className="mt-1 text-xs text-muted-foreground">
                      These fields are stored by the selected radio driver. DMR fields
                      appear when the channel mode is set to DMR.
                    </p>
                  </div>
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    {visibleEditorExtras.map((extra) => (
                      <label key={extra.name} className={extra.documentation ? 'sm:col-span-1' : ''}>
                        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          {extra.label}
                        </span>
                        {extra.kind === 'boolean' ? (
                          <span className="mt-1.5 flex min-h-10 items-center gap-3 rounded-lg border border-border bg-background px-3 text-sm">
                            <input
                              type="checkbox"
                              checked={Boolean(extra.value)}
                              disabled={extra.mutable === false}
                              onChange={(event) =>
                                setEditor(
                                  updateChannelExtra(
                                    editor,
                                    extra.name,
                                    event.target.checked,
                                  ),
                                )
                              }
                              className="h-4 w-4 accent-[var(--primary)]"
                            />
                            Enabled
                          </span>
                        ) : extra.kind === 'choice' ? (
                          <select
                            value={String(extra.value)}
                            disabled={extra.mutable === false}
                            onChange={(event) =>
                              setEditor(
                                updateChannelExtra(editor, extra.name, event.target.value),
                              )
                            }
                            className="mt-1.5 h-10 w-full rounded-lg border border-border bg-background px-3 text-sm outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20"
                          >
                            {(extra.choices ?? []).map((choice) => (
                              <option key={choice} value={choice}>
                                {choice}
                              </option>
                            ))}
                          </select>
                        ) : extra.kind === 'integer' || extra.kind === 'number' ? (
                          <input
                            type="number"
                            value={Number(extra.value)}
                            min={extra.minimum}
                            max={extra.maximum}
                            step={extra.kind === 'integer' ? 1 : 'any'}
                            disabled={extra.mutable === false}
                            onChange={(event) =>
                              setEditor(
                                updateChannelExtra(
                                  editor,
                                  extra.name,
                                  Number(event.target.value),
                                ),
                              )
                            }
                            className="mt-1.5 h-10 w-full rounded-lg border border-border bg-background px-3 font-mono text-sm outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20"
                          />
                        ) : (
                          <input
                            value={String(extra.value)}
                            disabled={extra.mutable === false}
                            onChange={(event) =>
                              setEditor(
                                updateChannelExtra(editor, extra.name, event.target.value),
                              )
                            }
                            className="mt-1.5 h-10 w-full rounded-lg border border-border bg-background px-3 text-sm outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20"
                          />
                        )}
                        {extra.documentation && (
                          <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">
                            {extra.documentation}
                          </span>
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button variant="outline" onClick={closeEditor}>
                Cancel
              </Button>
              <Button onClick={saveEditor} disabled={!editor.rxFrequency}>
                <Check className="h-4 w-4" />
                Apply to image
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
